"use client"

import { Col, Row } from "antd"
import { useState, useTransition, type FC } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { type Dayjs } from "dayjs"
import { toast } from "react-toastify"
import { useTERouter } from "@/hooks/app/useTERouter"
import SelectDestination, { type VisaDestinationValue } from "./SelectDestination"
import SelectVisaCategory from "./SelectVisaCategory"
import { DEFAULT_TRAVELER_COUNT, type VisaTravelerCount } from "./TravelerCountSelect"
import ButtonSearch from "./ButtonSearch"
import VisaPopularCountries from "./VisaPopularCountries"
import { getVisaPrograms } from "@/features/visa/api/visaApi"

type FormSearchVisaProps = {
  size?: "default" | "small"
  /** Điền sẵn ô search theo điều kiện đang xem — dùng ở trang kết quả tìm kiếm để đổi lại. */
  initialDestination?: VisaDestinationValue | null
  initialDepartureDate?: string
  initialTravelerCount?: VisaTravelerCount
  initialCategory?: string
  /** Báo cho trang cha biết một lượt tìm kiếm mới vừa bắt đầu, để hiện skeleton cho danh sách. */
  onSearchStart?: () => void
}

const FormSearchVisa: FC<FormSearchVisaProps> = ({
  size = "default",
  initialDestination = null,
  initialDepartureDate,
  initialTravelerCount,
  initialCategory,
  onSearchStart,
}) => {
  const { t } = useTranslation()
  const isSmall = size === "small"
  const router = useTERouter()
  const [destination, setDestination] = useState<VisaDestinationValue | null>(initialDestination)
  // Ngày đi + số người: API search chưa hỗ trợ lọc theo 2 field này (chỉ /visa/inquiries/submit
  // mới nhận), nên chỉ giữ lại trong URL để sau này điền sẵn vào form "Yêu cầu tư vấn" — không
  // ảnh hưởng kết quả tìm kiếm. Cả 2 field này không còn control trên UI (đã ẩn theo yêu cầu)
  // nhưng vẫn giữ nguyên giá trị ban đầu khi tìm lại, để không mất mát dữ liệu đã có trong URL.
  const [departureDate] = useState<Dayjs | null>(initialDepartureDate ? dayjs(initialDepartureDate) : null)
  const [travelerCount] = useState<VisaTravelerCount>(initialTravelerCount ?? DEFAULT_TRAVELER_COUNT)
  const [category, setCategory] = useState<string | undefined>(initialCategory)
  const [isNavigating, startNavigate] = useTransition()
  // Điều khiển dropdown SelectDestination từ bên ngoài — nút "Xem tất cả" ở hàng "Quốc gia
  // phổ biến" mở thẳng dropdown browse theo châu lục, không cần khách gõ trước.
  const [destinationOpen, setDestinationOpen] = useState(false)

  // Dùng chung cho cả nút bấm lẫn nhấn Enter trong ô input. Khi đã chọn đủ quốc gia cụ thể
  // và loại visa, lấy đúng chương trình rồi mở thẳng trang chi tiết; các trường hợp tìm kiếm
  // chưa đủ điều kiện vẫn đi qua trang kết quả để người dùng lọc tiếp.
  const handleSearch = async () => {
    const text = destination?.text?.trim()
    // Chỉ cần điền 1 trong 2 (điểm đến hoặc loại visa) là search được, không bắt buộc cả 2.
    if (!text && !category) {
      toast.error(t("visa.form_search.missing_selection_error"))
      return
    }

    const params = new URLSearchParams()
    if (text) params.set("keyword", text)
    // destination.code chỉ có khi khách chọn đúng 1 quốc gia trong danh sách gợi ý (không có
    // khi tự gõ tự do) — giúp trang kết quả lọc chính xác theo destinationCountryCode thay vì
    // chỉ dò theo từ khoá.
    if (destination?.code) params.set("destinationCountryCode", destination.code)
    if (category) params.set("category", category)
    if (departureDate) params.set("departureDate", departureDate.format("YYYY-MM-DD"))
    // Giữ riêng adult/child/infant (không chỉ tổng số) để ghép thành câu mô tả tự động
    // đưa vào "Ghi chú" của form tư vấn — API inquiry chỉ có 1 field travelerCount (số),
    // không có breakdown riêng.
    if (
      travelerCount.adult !== DEFAULT_TRAVELER_COUNT.adult ||
      travelerCount.child !== DEFAULT_TRAVELER_COUNT.child ||
      travelerCount.infant !== DEFAULT_TRAVELER_COUNT.infant
    ) {
      params.set("adultCount", String(travelerCount.adult))
      params.set("childCount", String(travelerCount.child))
      params.set("infantCount", String(travelerCount.infant))
    }

    onSearchStart?.()

    if (destination?.code && category) {
      try {
        const data = await getVisaPrograms({
          destinationCountryCode: destination.code,
          category: category as Parameters<typeof getVisaPrograms>[0]["category"],
          limit: 50,
        })
        const matchingProgram = data?.visaPrograms.find(
          (program) => program.destinationCountryCode === destination.code && program.category === category,
        )

        if (matchingProgram) {
          startNavigate(() => {
            router.push(`/dich-vu-visa/${matchingProgram.code}`)
          })
          return
        }
      } catch {
        // Fall through to the results page when the direct lookup fails.
      }
    }

    startNavigate(() => {
      router.push(`/dich-vu-visa/ket-qua-tim-kiem?${params.toString()}`)
    })
  }

  // Tab-bar trang chủ (compact) — giữ nguyên layout gốc: 1 hàng, destination + nút.
  if (isSmall) {
    return (
      <Row
        wrap={false}
        gutter={[0, 0]}
        align="stretch"
        className="home__searching--tour-search-row home__searching--tour-search-row--bar"
        style={{ marginBlock: 0 }}
      >
        <Col flex="1 1 auto" className="home__searching--tour-search-cell">
          <SelectDestination value={destination} onChange={setDestination} onPressEnter={handleSearch} compact />
        </Col>
        <Col flex="none" className="home__searching--tour-search-cell home__searching--tour-search-cell--btn">
          <ButtonSearch onClick={handleSearch} compact loading={isNavigating} />
        </Col>
      </Row>
    )
  }

  // Đứng riêng (trang /dich-vu-visa, trang kết quả) — bar 2 field (điểm đến/loại visa) + nút
  // tìm bên phải, đúng theo ảnh mockup. Tự responsive qua CSS (.visa__search-fields đổi hướng
  // cột trên màn hẹp, nút full-width) — không cần nhánh JSX riêng cho mobile/desktop như trước.
  return (
    <>
      <div className="visa__search-row" style={{ marginBlock: "2rem" }}>
        <div className="visa__search-fields">
          <SelectDestination
            value={destination}
            onChange={setDestination}
            onPressEnter={handleSearch}
            open={destinationOpen}
            onOpenChange={setDestinationOpen}
          />
          <div className="visa__search-divider" />
          <SelectVisaCategory value={category} onChange={setCategory} />
        </div>
        <div className="visa__search-button">
          <ButtonSearch onClick={handleSearch} loading={isNavigating} />
        </div>
      </div>

      <VisaPopularCountries onViewAll={() => setDestinationOpen(true)} />
    </>
  )
}

export default FormSearchVisa

